<template>
  <article id="lotto-wrapper" class="contents-wrapper">
    <div id="lotto-etc">
      <!-- 페이지 헤더 -->
      <div class="etc-head">
        <h2 class="etc-title">로또 도구</h2>
        <p class="etc-sub">당첨금 세금 계산과 자주 쓰는 공식 사이트 바로가기를 모았어요.</p>
      </div>

      <!-- 세금 계산기 -->
      <div class="section-card">
        <div class="section-title">당첨금 세금 계산기</div>
        <p class="section-desc">당첨금을 넣으면 세금을 뗀 실수령액을 계산해 드려요.</p>
        <div class="tax-input-row">
          <input
            type="number"
            v-model.number="prizeAmount"
            class="tax-input"
            placeholder="당첨금 입력 (원)"
          />
          <button class="tax-calc-btn" @click="calculateTax">계산</button>
        </div>
        <div class="tax-quick-btns">
          <button v-for="q in quickAmounts" :key="q.value" @click="prizeAmount = q.value; calculateTax()">
            {{ q.label }}
          </button>
        </div>
        <div v-if="taxResult !== null" class="tax-result">
          <div class="tax-result-row">
            <span class="tax-label">당첨금</span>
            <span class="tax-value">{{ formatKRW(taxResult.prize) }}</span>
          </div>
          <div class="tax-result-row">
            <span class="tax-label">세금 ({{ taxResult.taxRate }})</span>
            <span class="tax-value tax-deduct">-{{ formatKRW(taxResult.tax) }}</span>
          </div>
          <div class="tax-result-row total">
            <span class="tax-label">실수령액</span>
            <span class="tax-value tax-net">{{ formatKRW(taxResult.net) }}</span>
          </div>
          <div class="tax-net-ratio" v-if="taxResult.prize > 0">
            당첨금의 <b>{{ Math.round((taxResult.net / taxResult.prize) * 100) }}%</b>를 받아요
          </div>
        </div>
        <div class="tax-brackets">
          <div class="tax-brackets-title">세율 구간</div>
          <ul>
            <li><span>5만원 이하</span><b>비과세</b></li>
            <li><span>5만원 초과 ~ 3억원</span><b>22%</b></li>
            <li><span>3억원 초과분</span><b>33%</b></li>
          </ul>
          <p class="tax-brackets-note">소득세 + 주민세(지방소득세) 포함. 5만원 초과분에 대해 과세됩니다.</p>
        </div>
      </div>

      <!-- 등수별 당첨 조건 -->
      <div class="section-card">
        <div class="section-title">등수별 당첨 조건</div>
        <p class="section-desc">몇 개를 맞히면 몇 등인지, 당첨금은 어떻게 정해지는지 정리했어요.</p>
        <div class="rank-info-list">
          <div class="rank-info-item" v-for="info in rankInfoList" :key="info.rank">
            <div class="rank-badge" :class="'rank-' + info.rank">{{ info.rank }}등</div>
            <div class="rank-detail">
              <div class="rank-condition">{{ info.condition }}</div>
              <div class="rank-prize-info">{{ info.prizeDesc }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 공식 사이트 바로가기 -->
      <div class="section-card">
        <div class="section-title">공식 사이트 바로가기</div>
        <p class="section-desc">동행복권 공식 페이지로 이동합니다. 새 탭에서 열려요.</p>
        <div class="links-list">
          <a
            v-for="link in links"
            :key="link.url"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="link-item"
          >
            <div class="link-text">
              <span class="link-title">{{ link.title }}</span>
              <span class="link-desc">{{ link.desc }}</span>
            </div>
            <span class="link-arrow" aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import UtilMixin from "@/common/mixins/UtilMixin";

interface TaxResult {
  prize: number;
  tax: number;
  net: number;
  taxRate: string;
}

export default defineComponent({
  name: "LottoEtcView",
  mixins: [UtilMixin],
  data() {
    return {
      prizeAmount: 0,
      taxResult: null as TaxResult | null,
      quickAmounts: [
        { label: "5천원", value: 5000 },
        { label: "5만원", value: 50000 },
        { label: "1억", value: 100000000 },
        { label: "20억", value: 2000000000 },
      ],
      rankInfoList: [
        { rank: 1, condition: "6개 번호 모두 일치", prizeDesc: "총 당첨금의 75%" },
        { rank: 2, condition: "5개 + 보너스 번호 일치", prizeDesc: "총 당첨금의 12.5%" },
        { rank: 3, condition: "5개 번호 일치", prizeDesc: "총 당첨금의 12.5%" },
        { rank: 4, condition: "4개 번호 일치", prizeDesc: "고정 50,000원" },
        { rank: 5, condition: "3개 번호 일치", prizeDesc: "고정 5,000원" },
      ],
      links: [
        { title: "동행복권 공식 사이트", desc: "당첨 결과 확인·공지 등 메인", url: "https://www.dhlottery.co.kr" },
        { title: "판매점 찾기", desc: "가까운 로또 판매점 위치 검색", url: "https://www.dhlottery.co.kr/store.do?method=topStore&pageGubun=L645" },
        { title: "인터넷 구매 (로또 6/45)", desc: "온라인 구매 — 동행복권 로그인·예치금 필요", url: "https://ol.dhlottery.co.kr/olotto/game/game645.do" },
        { title: "회차별 당첨번호", desc: "지난 회차 당첨번호·당첨금 조회", url: "https://www.dhlottery.co.kr/gameResult.do?method=byWin" },
      ],
    };
  },
  methods: {
    formatKRW(amount: number): string {
      if (amount >= 100000000) {
        const uk = Math.floor(amount / 100000000);
        const rest = amount % 100000000;
        if (rest > 0) {
          return uk + "억 " + Math.round(rest / 10000).toLocaleString() + "만원";
        }
        return uk + "억원";
      } else if (amount >= 10000) {
        return Math.round(amount / 10000).toLocaleString() + "만원";
      }
      return amount.toLocaleString() + "원";
    },
    calculateTax() {
      if (!this.prizeAmount || this.prizeAmount <= 0) {
        this.taxResult = null;
        return;
      }
      const prize = this.prizeAmount;
      let tax = 0;
      let taxRate = "";

      if (prize <= 50000) {
        // 5만원 이하: 비과세
        tax = 0;
        taxRate = "비과세";
      } else if (prize <= 300000000) {
        // 3억 이하: 22% (소득세 20% + 주민세 2%)
        tax = Math.floor((prize - 50000) * 0.22);
        taxRate = "22%";
      } else {
        // 3억 초과: 3억까지 22%, 초과분 33%
        const under3 = Math.floor((300000000 - 50000) * 0.22);
        const over3 = Math.floor((prize - 300000000) * 0.33);
        tax = under3 + over3;
        taxRate = "22% + 33%";
      }

      this.taxResult = {
        prize,
        tax,
        net: prize - tax,
        taxRate,
      };
    },
  },
  created() {
    window.scrollTo(0, 0);
  },
});
</script>

<style lang="scss" scoped></style>
